import type { ReactNode } from 'react';
import { cx } from '../cx';
import { AnnotationMarkers, type ChartAnnotation } from './Annotations';
import { devWarn } from '../dev';
import { TICK_TEXT, VALUE_TEXT, chartStroke, niceScale, percentOf, sparseLabels } from './scale';
import { LineKey, NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { ROUGH_DOT, sampleTier } from './thin';

const GUTTER: Record<ChartLayout, string> = { auto: 'mr-2 @md:mr-28', narrow: 'mr-2', wide: 'mr-28' };

export type LineSeries = {
  id: string;
  /** Written at the line's end. */
  label: ReactNode;
  /** One value per x position; null is a gap (never drawn across). */
  values: Array<number | null>;
  /** The last value, formatted, written beside the end label of the story line. */
  lastDisplay?: ReactNode;
  /**
   * When each value is a median: how many values it rests on, per x position. Under 10 becomes a
   * gap; 10 to 19 is drawn with a hollow dashed point (rough).
   */
  samples?: Array<number | null>;
  /**
   * The middle half (p25 to p75) per x position, shaded behind the story line. Where a period is
   * rough the band fades, so the reader sees the spread is less sure there.
   */
  band?: Array<[number, number] | null>;
};

export type LineChartProps = {
  /** The x positions, oldest first ("Sep", "Oct", ...). */
  x: ReactNode[];
  /** One to four lines. */
  series: LineSeries[];
  /** The story line: ink, thicker, with a sun marker at its end. The rest are context. With one series it is that one. */
  highlightId?: string;
  /** Start the y-axis at zero (default). When false, say so in the frame's subtitle. */
  zero?: boolean;
  formatTick: (value: number) => string;
  /** Events marked on the chart, numbered in order; list them under it with AnnotationList. */
  annotations?: ChartAnnotation[];
  /** `auto` (default) switches to the narrow form in a narrow container: line names and the last value move to a key under the chart. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

/** Split a series into runs without gaps, as polyline points in a 0 to 100 box. */
function runs(values: Array<number | null>, min: number, max: number): string[] {
  const out: string[][] = [];
  let current: string[] = [];
  values.forEach((v, i) => {
    if (v == null) {
      if (current.length) out.push(current);
      current = [];
      return;
    }
    const xPos = values.length === 1 ? 50 : (i / (values.length - 1)) * 100;
    current.push(`${xPos},${100 - percentOf(v, min, max)}`);
  });
  if (current.length) out.push(current);
  return out.map((r) => r.join(' '));
}

/** Push end labels apart so they never overlap (in percent of the plot's height). */
export function spreadLabels(positions: number[], gap = 9): number[] {
  const order = positions.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  for (let k = 1; k < order.length; k++) {
    if (order[k].p - order[k - 1].p < gap) order[k].p = order[k - 1].p + gap;
  }
  const out = [...positions];
  for (const { p, i } of order) out[i] = p;
  return out;
}

/**
 * Lines for how a rate or value moved over time, one to four series. Line names sit at the line
 * ends; gaps in the data stay gaps. Counts per period belong in a ColumnChart.
 */
export function LineChart({
  x,
  series: rawSeries,
  highlightId,
  zero = true,
  formatTick,
  annotations,
  layout = 'auto',
  description,
  className,
}: LineChartProps) {
  devWarn(rawSeries.length > 4, `LineChart has ${rawSeries.length} lines; four at most, or use small multiples.`);
  // Medians on fewer than 10 values are not drawn: they become gaps.
  const series = rawSeries.map((s) => {
    const tiers = s.values.map((_, i) => (s.samples?.[i] == null ? 'full' : sampleTier(s.samples[i] as number)));
    return {
      ...s,
      tiers,
      values: s.values.map((v, i) => (tiers[i] === 'none' ? null : v)),
      band: s.band?.map((b, i) => (tiers[i] === 'none' ? null : b)),
    };
  });
  const all = series.flatMap((s) => [
    ...s.values.filter((v): v is number => v != null),
    ...(s.band ?? []).flatMap((b) => b ?? []),
  ]);
  const scale = niceScale(Math.min(...all), Math.max(...all), { zero });
  const story = highlightId ?? (series.length === 1 ? series[0].id : undefined);
  const lastY = series.map((s) => {
    const last = [...s.values].reverse().find((v) => v != null);
    return last == null ? 0 : 100 - percentOf(last, scale.min, scale.max);
  });
  const labelY = spreadLabels(lastY);
  const showLabel = sparseLabels(x.length);
  const xOf = (i: number) => (x.length === 1 ? 50 : (i / (x.length - 1)) * 100);
  const yOf = (v: number) => 100 - percentOf(v, scale.min, scale.max);
  const storySeries = series.find((s) => s.id === story);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="flex gap-2">
        <div className="relative h-48 w-12 shrink-0" aria-hidden="true">
          {scale.ticks.map((t) => (
            <span
              key={t}
              className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)}
              style={{ bottom: `${percentOf(t, scale.min, scale.max)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
        <div className={cx('relative h-48 flex-1', GUTTER[layout])}>
          {annotations?.length ? (
            <AnnotationMarkers
              annotations={annotations}
              positionOf={(i) => (x.length === 1 ? 50 : (i / (x.length - 1)) * 100)}
            />
          ) : null}
          {scale.ticks.map((t) => (
            <span
              key={t}
              className="absolute inset-x-0 border-t border-jc-chart-grid"
              style={{ bottom: `${percentOf(t, scale.min, scale.max)}%` }}
            />
          ))}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 size-full overflow-visible motion-safe:animate-jc-fade"
          >
            {/* The story line's middle half, one piece per interval so rough periods can fade. */}
            {storySeries?.band?.slice(0, -1).map((b, i) => {
              const next = storySeries.band?.[i + 1];
              if (!b || !next) return null;
              const faint = storySeries.tiers[i] !== 'full' || storySeries.tiers[i + 1] !== 'full';
              return (
                <polygon
                  key={`band-${i}`}
                  points={`${xOf(i)},${yOf(b[1])} ${xOf(i + 1)},${yOf(next[1])} ${xOf(i + 1)},${yOf(next[0])} ${xOf(i)},${yOf(b[0])}`}
                  className={cx('fill-jc-chart-context', faint ? 'opacity-25' : 'opacity-60')}
                />
              );
            })}
            {/* The story line is drawn last, on top. */}
            {[...series.filter((s) => s.id !== story), ...series.filter((s) => s.id === story)].flatMap((s) =>
              runs(s.values, scale.min, scale.max).map((points, k) => (
                <polyline
                  key={`${s.id}-${k}`}
                  points={points}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  strokeWidth={s.id === story ? 2.5 : 1.75}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  className={chartStroke(s.id === story ? 'subject' : 'context')}
                />
              )),
            )}
          </svg>
          {series.flatMap((s) =>
            s.tiers.map((t, i) =>
              t === 'rough' && s.values[i] != null ? (
                <span
                  key={`${s.id}-rough-${i}`}
                  aria-hidden="true"
                  className={cx('absolute size-2.5 -translate-x-1/2 -translate-y-1/2', ROUGH_DOT)}
                  style={{ left: `${xOf(i)}%`, top: `${yOf(s.values[i] as number)}%` }}
                />
              ) : null,
            ),
          )}
          {series.map((s, i) => {
            const isStory = s.id === story;
            return (
              <span key={s.id} aria-hidden="true">
                {isStory ? (
                  <span
                    className="absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-jc-chart-2 ring-2 ring-jc-ink"
                    style={{ left: '100%', top: `${lastY[i]}%` }}
                  />
                ) : null}
                <span
                  className={cx(
                    'absolute left-full flex w-28 -translate-y-1/2 flex-col pl-3 leading-tight',
                    WIDE_ONLY[layout],
                    isStory ? VALUE_TEXT : 'text-xs text-jc-ink-secondary',
                  )}
                  style={{ top: `${labelY[i]}%` }}
                >
                  {isStory && s.lastDisplay ? <span>{s.lastDisplay}</span> : null}
                  {series.length > 1 ? <span className="truncate">{s.label}</span> : null}
                </span>
              </span>
            );
          })}
        </div>
      </div>
      <div className={cx('relative ml-14 h-4', GUTTER[layout])} aria-hidden="true">
        {x.map((label, i) =>
          showLabel[i] ? (
            <span
              key={i}
              className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT)}
              style={{ left: `${x.length === 1 ? 50 : (i / (x.length - 1)) * 100}%` }}
            >
              {label}
            </span>
          ) : null,
        )}
      </div>
      <LineKey
        className={cx('pt-1', NARROW_ONLY[layout])}
        items={series.map((s) => ({
          id: s.id,
          label: s.label,
          story: s.id === story,
          value: s.id === story ? s.lastDisplay : undefined,
        }))}
      />
    </div>
  );
}
